import React, { InputHTMLAttributes } from "react";
import classNames from "classnames";
import Link, { LinkProps } from "next/link";

type CheckboxProps = InputHTMLAttributes<HTMLInputElement> & {
  id: string;
  children?: React.ReactNode;
};

const CheckboxBody: React.FC<CheckboxProps> = ({ children, ...inputProps }) => (
  <>
    <input
      className="size-[1.125rem] min-w-[1.125rem] focus:outline-none focus:shadow-outline accent-default mr-sm"
      type="checkbox"
      // without `readonly` set, it'll throw an error in jest, but this doesn't do anything for checkboxes.
      // checking the checkbox modifies the field's state, not value; so `readonly` doesn't apply.
      readOnly
      {...inputProps}
    />
    <label
      // If an element is used, it doesn't get this line height so the [&_*] enforces it for all descendents.
      // You can override it with ![&_span]:leading-[1.5rem] for example if you're adding a span
      className="leading-[1.25rem] [&_*]:leading-[1.25rem]"
      htmlFor={inputProps.id}
    >
      {children}
    </label>
  </>
);

type Props = CheckboxProps & {
  labelOrder?: "before" | "after";
  href?: LinkProps["href"];
  rel?: string;
  onChecked?: () => void;
};

const Checkbox: React.FC<Props> = ({
  labelOrder = "after",
  href,
  className = "",
  rel,
  onChecked,
  ...inputProps
}) => {
  if (!href && rel) {
    console.warn(
      "Checkbox: `rel` prop is only applicable when `href` is provided.",
    );
  }

  const classes = classNames(
    className,
    "text-sm w-full min-h-8 flex items-start cursor-pointer [&>*]:cursor-pointer",
    {
      "aria-disabled:pointer-events-none cursor-default opacity-50":
        inputProps.disabled,
      "flex-row": labelOrder === "after",
      "flex-row-reverse justify-between": labelOrder === "before",
    },
  );

  const sharedProps = {
    "aria-disabled": inputProps.disabled,
    className: classes,
  };

  return href ? (
    <Link
      {...sharedProps}
      href={href}
      rel={rel}
      onClick={() => {
        if (!inputProps.checked && onChecked) {
          onChecked();
        }
      }}
    >
      <CheckboxBody {...inputProps} />
    </Link>
  ) : (
    <div {...sharedProps}>
      <CheckboxBody {...inputProps} />
    </div>
  );
};

export default Checkbox;
